//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import {
  FlatDefaultButton,
  PrimaryButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import SmartInput from '@components/molecules/smart-input/SmartInput';
import Cuid from '@models/Cuid';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useRemoveCaptionMutation } from '@uia/details/hooks/useRemoveCaptionMutation';
import { useUpdateCaptionMutation } from '@uia/details/hooks/useUpdateCaptionMutation';

export interface PreferredCaptionEditorProps {
  caption: string;
  cuid: Cuid;
  hasPreferredCaption: boolean;
  onCancel: () => void;
  onRevert: () => void;
  onSave: () => void;
}

export const PreferredCaptionEditor = ({
  caption,
  cuid,
  hasPreferredCaption,
  onCancel,
  onRevert,
  onSave,
}: PreferredCaptionEditorProps) => {
  const [captionValue, setCaptionValue] = useState(caption);
  const { isLoading: isRemoving, mutate: removeCaption } =
    useRemoveCaptionMutation(cuid);

  const { isLoading: isUpdating, mutate: updateCaption } =
    useUpdateCaptionMutation();

  return (
    <>
      <Title>Preferred Caption</Title>
      <InputWrapper>
        <LabelWrapper>
          <BodyText>
            Creating a Preferred Caption will change the caption of media that
            is owned by the end user. This caption will be shown in your onsite
            experiences.
          </BodyText>
        </LabelWrapper>
        <SmartInput
          numRows={6}
          onChange={setCaptionValue}
          trackingIdPrefix={UIATrackingIds.DETAILS_EDIT_CAPTION_INPUT}
          value={captionValue}
        />
      </InputWrapper>
      <ButtonWrapper>
        {hasPreferredCaption && (
          <PrimaryLinkButton
            onClick={() =>
              removeCaption(undefined, { onSettled: () => onRevert() })
            }
          >
            <AlignedText>
              Revert to Original {isRemoving && <InlineSpinner />}
            </AlignedText>
          </PrimaryLinkButton>
        )}
        <CancelButton onClick={onCancel}>Cancel</CancelButton>
        <PrimaryButton
          onClick={() => {
            updateCaption(
              { caption: captionValue, cuid },
              { onSettled: () => onSave() },
            );
          }}
        >
          <AlignedText>
            Save Changes {isUpdating && <InlineSpinner />}
          </AlignedText>
        </PrimaryButton>
      </ButtonWrapper>
    </>
  );
};

const LabelWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  padding: ${theme.spacings[2]} ${theme.spacings[0]};
`,
);

const Title = styled(BodyBoldText)(
  ({ theme }) => `
    margin-bottom: ${theme.spacings[2]};
  `,
);

const InputWrapper = styled.div(
  ({ theme }) => `
    margin-top: ${theme.spacings[2]};
  `,
);

const CancelButton = styled(FlatDefaultButton)`
  margin-left: auto;
  margin-right: ${({ theme }) => theme.spacings[2]};
`;

const ButtonWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    margin-top: ${theme.spacings[2]};
  `,
);

const AlignedText = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);
